// src/layout/builders/ProfileDetails/AddressDetails.jsx
import { useEffect, useState } from "react";
import useProfileStore from "../../../store/builders/useProfileStore";
import { MapContainer, TileLayer, Marker, useMap } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import L from "leaflet";
import { toast } from "react-hot-toast";

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require("leaflet/dist/images/marker-icon-2x.png"),
  iconUrl: require("leaflet/dist/images/marker-icon.png"),
  shadowUrl: require("leaflet/dist/images/marker-shadow.png"),
});

const MarkerPosition = ({ position }) => {
  const map = useMap();
  useEffect(() => {
    if (position && typeof position[0] === "number" && typeof position[1] === "number") {
      map.setView(position, 15);
    }
  }, [position, map]);
  return null;
};

const AddressDetails = () => {
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({});
  const { profile, fetchProfile, updateProfile, isLoading, isUpdating } =
    useProfileStore();

  // Load profile initially
  useEffect(() => {
    if (!profile) {
      fetchProfile();
    }
  }, [profile, fetchProfile]);

  // Sync profile → formData
  useEffect(() => {
    if (profile?.builder && !isEditing) {
      console.log("AddressDetails: Full profile object", profile); // DEBUG LOG

      // Address fields are flattened directly on profile (not profile.builder)
      setFormData({
        address_line1: profile.builder.address_line1 || "",
        address_line2: profile.builder.address_line2 || "",
        village: profile.builder.village || "",
        taluk: profile.builder.taluk || "",
        district: profile.builder.district || "",
        state: profile.builder.state || "Tamil Nadu",
        postcode: profile.builder.postcode || "",
        latitude: profile.builder.latitude ? profile.builder.latitude.toString() : "",
        longitude: profile.builder.longitude ? profile.builder.longitude.toString() : ""
      });

      console.log("AddressDetails: FormData set to", {
        address_line1: profile.builder.address_line1,
        taluk: profile.builder.taluk,
        district: profile.builder.district
      }); // DEBUG LOG
    }
  }, [profile, isEditing]);


  const toggleEdit = () => {
    setIsEditing(!isEditing);
  };

  const handleInputChange = (key, value) => {
    let processedValue = value;

    if (key === "postcode") {
      processedValue = value.replace(/\D/g, "").slice(0, 6);
    } else if (key === "latitude" || key === "longitude") {
      processedValue = value.replace(/[^\d.-]/g, "");
    }

    setFormData((prev) => ({
      ...prev,
      [key]: processedValue,
    }));
  };

  const validateForm = () => {
    if (!formData.address_line1?.trim()) return toast.error("Address Line 1 is required.");
    if (!formData.taluk?.trim()) return toast.error("Taluk is required.");
    if (!formData.district?.trim()) return toast.error("District is required.");
    if (!formData.postcode?.trim()) return toast.error("Postcode is required.");
    if (formData.postcode.length !== 6) return toast.error("Postcode must be 6 digits.");
    if (formData.latitude && isNaN(parseFloat(formData.latitude))) return toast.error("Latitude must be numeric.");
    if (formData.longitude && isNaN(parseFloat(formData.longitude))) return toast.error("Longitude must be numeric.");
    return true;
  };

  const addressFieldsOrder = [
    { key: "address_line1", label: "Address Line 1", required: true, readOnly: true },
    { key: "address_line2", label: "Address Line 2", readOnly: true },
    { key: "village", label: "Village/Town", readOnly: true },
    { key: "taluk", label: "Taluk", required: true, readOnly: true },
    { key: "district", label: "District", required: true, readOnly: true },
    { key: "state", label: "State", required: true, readOnly: true },
    { key: "postcode", label: "Postcode", type: "tel", required: true, readOnly: true },
    { key: "latitude", label: "Latitude", type: "number", readOnly: true },
    { key: "longitude", label: "Longitude", type: "number", readOnly: true },
  ];

  if (isLoading && !profile) {
    return (
      <div className="flex justify-center items-center py-6">
        <div className="w-6 h-6 border-2 border-blue-500 border-dashed rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="py-2 text-xs sm:text-sm">
      {/* Header */}
      <div className="flex items-center justify-start gap-4 mb-4 border-b border-gray-400 pb-3">
        <h2 className="text-sm font-semibold">Address Details</h2>
      </div>

      {/* Inputs */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
        {addressFieldsOrder.map(({ key, label, type, readOnly, required }) => (
          <div key={key} className="space-y-1.5">
            <label className={`block text-xs sm:text-sm font-medium ${isEditing && required && !formData[key] ? "text-red-500" : "text-gray-600"}`}>
              {label} {required && isEditing && <span className="text-red-500">*</span>}
            </label>

            <input
              type={type === "number" && isEditing ? "number" : "text"}
              inputMode={type === "number" ? "decimal" : key === "postcode" ? "numeric" : "text"}
              value={
                key === "state"
                  ? formData.state || "Tamil Nadu"
                  : formData[key] || ""
              }
              onChange={(e) => handleInputChange(key, e.target.value)}
              disabled={!isEditing || readOnly || isUpdating}
              className={`w-full p-2 sm:p-2.5 rounded-md text-xs sm:text-sm transition-colors outline-none ${!isEditing || readOnly
                ? !formData[key] && required
                  ? "border border-red-500 bg-red-50 text-red-600 cursor-default"
                  : "bg-gray-50 border-transparent cursor-default text-gray-800"
                : isEditing && required && !formData[key]
                  ? "border border-red-500 bg-white text-gray-900 focus:ring-1 focus:ring-red-100"
                  : "border border-gray-300 bg-white text-gray-900 focus:border-blue-500 focus:ring-1 focus:ring-blue-100"
                }`}
            />
          </div>
        ))}
      </div>

      {/* Map */}
      <div className="mt-4 sm:mt-6 space-y-3">
        <label className="block text-xs sm:text-sm font-medium text-gray-600">Location on Map</label>

        <MapContainer
          center={
            formData.latitude && formData.longitude
              ? [parseFloat(formData.latitude), parseFloat(formData.longitude)]
              : [20.5937, 78.9629]
          }
          zoom={formData.latitude && formData.longitude ? 15 : 5}
          style={{ height: "200px", width: "100%" }}
          className="border rounded-md overflow-hidden h-[200px] sm:h-[250px]"
          key={`${formData.latitude}-${formData.longitude}`}
        >
          <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />

          {formData.latitude && formData.longitude && (
            <>
              <Marker
                position={[
                  parseFloat(formData.latitude),
                  parseFloat(formData.longitude),
                ]}
                draggable={false}
              />
              <MarkerPosition
                position={[
                  parseFloat(formData.latitude),
                  parseFloat(formData.longitude),
                ]}
              />
            </>
          )}
        </MapContainer>

        {/* Current Location button removed since address is non-editable */}
      </div>
    </div>
  );
};

export default AddressDetails;
